import { Box, Collapse, Divider, styled, Typography } from "@mui/material";
import React from "react";

const Circle = styled(Box)(({ theme }) => ({
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  flexShrink: 0,
  marginTop: theme.spacing(0.75),
}));

const ActiveCircle = styled(Circle)(({ theme }) => ({
  border: `2px solid ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.default,
}));

const PassedCircle = styled(Circle)(({ theme }) => ({
  background: theme.palette.primary.main,
}));

const InactiveCircle = styled(Circle)(({ theme }) => ({
  border: `2px solid ${theme.palette.border.primary}`,
  backgroundColor: theme.palette.background.default,
}));

export const StepInnerContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSubstate" &&
    prop !== "isActiveParent" &&
    prop !== "isActive" &&
    prop !== "isLast" &&
    prop !== "displaySubsteps",
})<{
  displaySubsteps?: boolean;
  isSubstate?: boolean;
  isLast?: boolean;
  isActiveParent?: boolean;
  isActive?: boolean;
}>(
  ({
    theme,
    isSubstate,
    isLast,
    isActive,
    isActiveParent,
    displaySubsteps,
  }) => ({
    paddingBottom: theme.spacing(
      isSubstate && !isLast
        ? 2
        : (isActiveParent || isLast) && displaySubsteps
          ? 0
          : 6
    ),

    ...(isActive &&
      isSubstate && {
        display: "flex",
        alignItems: "center",
        marginLeft: theme.spacing(-4.25),

        "&:before": {
          content: '""',
          display: "block",
          position: "relative",
          height: "100%",
          width: "1px",
          backgroundColor: theme.palette.primary.main,
          marginRight: theme.spacing(4),
        },
      }),
  })
);

export type Step = {
  id: string;
  title?: string;
  onClick?: (id: string) => void;
  substeps?: Step[];
};

export const StepContainer = ({
  step,
  isLast,
  isSubstate,
  state,
  activeStep,
  onClick,
  displaySubsteps,
}: {
  step: Step;
  isLast?: boolean;
  isSubstate?: boolean;
  activeStep: string;
  state: string;
  onClick?: (id: string) => void;
  displaySubsteps?: boolean;
}) => {
  return (
    <Box>
      <Box display="flex" gap={2} className="step">
        {!isSubstate && (
          <Box flexShrink="0" mb={1.75}>
            {state === "passed" && <PassedCircle />}
            {state === "active" && <ActiveCircle />}
            {state === "next" && <InactiveCircle />}
            {!isLast && (
              <Divider
                orientation="vertical"
                sx={{
                  height: "calc(100% - 23px)",
                  width: "1px",
                  borderColor: (theme) => theme.palette.border.primary,
                  margin: "7px 0 0 7px",
                }}
              />
            )}
          </Box>
        )}
        <StepInnerContainer
          onClick={() => onClick?.(step.id)}
          isSubstate={isSubstate}
          isLast={isLast}
          isActive={state === "active"}
          isActiveParent={step.substeps && state === "active"}
          sx={{ cursor: onClick ? "pointer" : "auto" }}
          displaySubsteps={displaySubsteps}
        >
          <Typography
            variant="body3"
            color={
              state === "next"
                ? "text.tertiary"
                : state === "passed"
                  ? "textSecondary"
                  : "textPrimary"
            }
          >
            {step.title}
          </Typography>
          {step.substeps && displaySubsteps && (
            <Collapse in={state === "active"}>
              <Box>
                <SidebarSteps
                  steps={step.substeps}
                  activeStep={activeStep}
                  isSubstate
                  onClick={onClick}
                />
              </Box>
            </Collapse>
          )}
        </StepInnerContainer>
      </Box>
    </Box>
  );
};

export const SidebarSteps = ({
  steps,
  isSubstate = false,
  activeStep,
  onClick,
  displaySubsteps = true,
}: {
  steps: Step[];
  activeStep?: string;
  isSubstate?: boolean;
  onClick?: (id: string) => void;
  displaySubsteps?: boolean;
}) => {
  const activeStepIndex = activeStep
    ? steps.findIndex((step) => {
        const isSubstepActive = step.substeps?.find(
          (substep: Step) => substep.id === activeStep
        );
        return step.id === activeStep || Boolean(isSubstepActive);
      })
    : 0;

  return (
    <Box pt={isSubstate ? 3 : 0} pb={isSubstate ? 3 : 0}>
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        let state =
          i < activeStepIndex
            ? "passed"
            : i > activeStepIndex
              ? "next"
              : "active";

        return (
          <StepContainer
            step={step}
            state={state}
            key={step.id}
            isLast={isLast}
            isSubstate={isSubstate}
            activeStep={activeStep || steps[0].id}
            onClick={onClick}
            displaySubsteps={displaySubsteps}
          />
        );
      })}
    </Box>
  );
};
